import configuration from '../configuration';
/* cspell:words describedby */
import { createStyleTag } from '../util/create-style-tag';
import tooltipCss from './css/tooltip.css';
import Cta from './index';
import { Instance, createPopper } from '@popperjs/core';
import Asset from '../asset/asset';

const TOOLTIP_CLOSE_DELAY_MS = 150;
let nextTooltipId = 0;

/**
 * Displays a tooltip to the User describing how the monthly payment is calculated, and some disclaimers.
 */
export default class OlToolTip<AssetType extends Asset> {
  component: HTMLElement;
  text: HTMLElement;
  open = false;
  popperInstance: Instance;
  private closeTimer?: number;
  private pinned = false;
  private pointerOnTarget = false;
  private pointerOnTooltip = false;
  private targetFocused = false;
  private tooltipFocused = false;
  private suppressNextFocusOpen = false;
  private closeButton: HTMLButtonElement;

  /**
   * Create a new Tooltip HTML Element
   * @param asset: Asset that is being financed
   * @param cta: CTA Class to display Tooltip on.
   *             This is required to display the tooltip in the correct location.
   * @param target: HTML Element that the Tooltip should display when clicked (mobile) or hovered (desktop)
   * @param price: Price used to calculate the monthly payment.
   */
  constructor(
    public asset: AssetType,
    protected cta: Cta<AssetType>,
    public target: HTMLElement,
    public price: number
  ) {
    const tooltipId = ++nextTooltipId;

    this.component = document.createElement('div');
    this.component.classList.add('ol-tooltip');
    this.component.id = `ol-payment-tooltip-${tooltipId}`;
    this.component.hidden = true;
    this.component.appendChild(createStyleTag(tooltipCss.toString(), 'ol-tooltip-style', {}));

    this.closeButton = document.createElement('button');
    this.closeButton.type = 'button';
    this.closeButton.classList.add('ol-body', 'ol-tooltip-text', 'ol-inline', 'ol-close-text');
    this.closeButton.textContent = 'CLOSE \u00d7';
    this.closeButton.setAttribute('aria-label', 'Close payment details');
    this.closeButton.addEventListener('click', () => {
      this.suppressNextFocusOpen = true;
      this.closeTooltip();
      this.target.focus({ preventScroll: true });
    });
    this.closeButton.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab') return;

      if (event.shiftKey) {
        event.preventDefault();
        this.target.focus({ preventScroll: true });
        return;
      }

      this.closeTooltip();
      const actions = [
        ...this.cta.shadowRoot.querySelectorAll<HTMLElement>(
          'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ].filter((action) => !this.component.contains(action));
      const nextAction = actions[actions.indexOf(this.target) + 1];
      if (nextAction) {
        event.preventDefault();
        nextAction.focus({ preventScroll: true });
      }
    });
    this.component.appendChild(this.closeButton);

    const description = document.createElement('div');
    description.id = `ol-payment-tooltip-description-${tooltipId}`;
    description.setAttribute('role', 'tooltip');

    const title = document.createElement('p');
    title.classList.add('ol-title', 'ol-tooltip-text', 'ol-inline');
    title.textContent = 'Payment Shown';
    description.appendChild(title);

    this.text = document.createElement('p');
    this.text.classList.add('ol-body', 'ol-tooltip-text', 'ol-tooltip-text-body');
    description.appendChild(this.text);
    this.component.appendChild(description);

    this.target.setAttribute('aria-controls', this.component.id);
    this.target.setAttribute('aria-describedby', description.id);
    this.target.setAttribute('aria-expanded', 'false');
    this.cta.shadowRoot.appendChild(this.component);

    this.popperInstance = createPopper(this.cta.shadowHost, this.component, {
      placement: 'top',
      strategy: 'absolute',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 7]
          }
        },
        {
          // custom modifier to match the width of the tooltip to the width of the widget
          name: 'sameWidth',
          enabled: true,
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          fn: ({ state }) => {
            state.styles.popper.width = `${state.rects.reference.width}px`;
          },
          effect: ({ state }) => {
            state.elements.popper.style.width = `${
              //@ts-ignore offsetWidth does not exist on elements
              state.elements.offsetWidth
            }px`;
          }
        }
      ]
    });

    this.target.addEventListener('click', () => {
      if (this.pinned) {
        this.closeTooltip();
      } else {
        this.pinned = true;
        this.openTooltip();
      }
    });
    this.target.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || event.shiftKey || !this.open) return;
      if (window.getComputedStyle(this.closeButton).display === 'none') return;
      event.preventDefault();
      this.closeButton.focus({ preventScroll: true });
    });

    this.target.addEventListener('pointerenter', () => {
      this.pointerOnTarget = true;
      this.cancelScheduledClose();
      this.openTooltip();
    });
    this.target.addEventListener('pointerleave', () => {
      this.pointerOnTarget = false;
      this.scheduleClose();
    });
    this.component.addEventListener('pointerenter', () => {
      this.pointerOnTooltip = true;
      this.cancelScheduledClose();
    });
    this.component.addEventListener('pointerleave', () => {
      this.pointerOnTooltip = false;
      this.scheduleClose();
    });

    this.target.addEventListener('focus', () => {
      this.targetFocused = true;
      this.cancelScheduledClose();
      if (this.suppressNextFocusOpen) {
        this.suppressNextFocusOpen = false;
        return;
      }
      this.openTooltip();
    });
    this.target.addEventListener('blur', () => {
      this.targetFocused = false;
      this.scheduleClose();
    });
    this.component.addEventListener('focusin', () => {
      this.tooltipFocused = true;
      this.cancelScheduledClose();
    });
    this.component.addEventListener('focusout', (event: FocusEvent) => {
      this.tooltipFocused = event.relatedTarget instanceof Node && this.component.contains(event.relatedTarget);
      this.scheduleClose();
    });
  }

  private readonly handleKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !this.open) return;
    event.preventDefault();
    this.closeTooltip();
  };

  private cancelScheduledClose() {
    if (this.closeTimer === undefined) return;
    window.clearTimeout(this.closeTimer);
    this.closeTimer = undefined;
  }

  private scheduleClose() {
    this.cancelScheduledClose();
    if (
      !this.open ||
      this.pinned ||
      this.pointerOnTarget ||
      this.pointerOnTooltip ||
      this.targetFocused ||
      this.tooltipFocused
    ) {
      return;
    }

    this.closeTimer = window.setTimeout(() => {
      this.closeTimer = undefined;
      if (
        !this.pinned &&
        !this.pointerOnTarget &&
        !this.pointerOnTooltip &&
        !this.targetFocused &&
        !this.tooltipFocused
      ) {
        this.closeTooltip();
      }
    }, TOOLTIP_CLOSE_DELAY_MS);
  }

  /**
   * Stop displaying the Tooltip on the Webpage
   */
  closeTooltip() {
    this.cancelScheduledClose();
    this.component.removeAttribute('data-show');
    this.component.hidden = true;
    this.target.setAttribute('aria-expanded', 'false');
    this.pinned = false;
    this.open = false;
    document.removeEventListener('keydown', this.handleKeydown);
  }

  destroy() {
    this.closeTooltip();
    this.popperInstance.destroy();
    this.component.remove();
  }

  /**
   * Display the tooltip on the webpage
   */
  openTooltip() {
    this.cancelScheduledClose();

    const totalPrice = this.price + configuration.doc_fee;
    const totalFormattedPrice = totalPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    // Re-generate the text in case a value has changed (ex, docFee)
    this.text.textContent =
      `Monthly payment calculated based upon a ${configuration.term_months} month loan, at ${configuration.apr_percent}% APR, and $${totalFormattedPrice} total ` +
      (configuration.doc_fee === 0
        ? `amount financed. Excludes tax, title, license, and doc fees. Not all buyers or vehicles will qualify.
    Minimum $100 monthly pmt required. `
        : `amt financed. Incl $${configuration.doc_fee} doc fee & excludes tax, title & license fees. Not all buyers or vehicles will qualify.
    Min $100 monthly pmt required. `) +
      `Terms subject to change without notice.`;

    this.component.hidden = false;
    this.component.setAttribute('data-show', '');
    this.target.setAttribute('aria-expanded', 'true');
    this.open = true;
    document.addEventListener('keydown', this.handleKeydown);
    void this.popperInstance.update();
  }
}
